// A URL taken apart, spelled exactly as the text had it: nothing here is decoded, because the address is what the
// components are a view of. Only the query builder unescapes, and it is the one thing on the page that does.
export interface UrlParts {
  scheme: string;
  // Whether the source carried the `//` that opens an authority, so `mailto:` keeps its shape and `//host` keeps its.
  slashes: boolean;
  username: string;
  password: string;
  host: string;
  port: string;
  path: string;
  // Null where there was no `?` at all, which the empty string is not: `https://x/?` carries a query with nothing in it.
  query: string | null;
  fragment: string | null;
}

export type PartKey = "scheme" | "username" | "password" | "host" | "port" | "path" | "query" | "fragment";

// One parameter of the query, as the builder shows it and as the address holds it.
export interface Pair {
  // Decoded, which is the whole of what the builder is for; the query box above it shows what the address carries.
  name: string;
  value: string;
  // Written without an `=`: a flag rather than a name with nothing after it, and rewriting it as `name=` says more.
  bare: boolean;
  // Exactly the text between the separators, kept so a parameter nobody touched is written back byte for byte.
  raw: string;
  nameError: string | null;
  valueError: string | null;
}

export interface UrlReading {
  parts: UrlParts;
  pairs: Pair[];
  partErrors: Record<PartKey, string | null>;
}

export interface PartSpec {
  key: PartKey;
  label: string;
  // A component of the address rather than of any particular one, so the placeholder is what the piece looks like.
  placeholder: string;
  hint?: string;
}

export const PART_ROWS: PartSpec[][] = [
  [
    { key: "scheme", label: "Scheme", placeholder: "https" },
    { key: "host", label: "Host", placeholder: "example.com" },
    { key: "port", label: "Port", placeholder: "443" },
  ],
  [
    { key: "username", label: "Username", placeholder: "user" },
    { key: "password", label: "Password", placeholder: "secret" },
  ],
  [
    { key: "path", label: "Path", placeholder: "/path/to/page" },
    { key: "fragment", label: "Fragment", placeholder: "section" },
  ],
];

// The query sits in the card the builder is in rather than with the other components, the two being one thing twice.
export const QUERY_PART: PartSpec = {
  key: "query",
  label: "Query",
  placeholder: "a=1&b=2",
  hint: "The parameters below are this same query, unescaped",
};

// A value typed into any of these four is what opens an authority on an address that never had one.
export const AUTHORITY_PARTS = new Set<PartKey>(["username", "password", "host", "port"]);

// Carries every component at once, since an empty page shows nothing of what the boxes under it are for; the space
// and the é are there because the builder reading them back as text is the thing worth seeing first.
export const DEFAULT_URL = "https://example.com:8443/api/v2/search?q=caf%C3%A9+latte&limit=20&tags=hot,fast#results";

export function partText(parts: UrlParts, key: PartKey): string {
  return parts[key] ?? "";
}
